package com.neurobs.ccabserver.subjectsessionview.components.sessionview

import com.neurobs.ccab.{SessionDisplayData, TestSessionProperties}
import com.neurobs.ccabserver.subjectsessionview.components.PropertyLabel
import com.neurobs.ccabserver.subjectsessionview.states.{SessionSelectionStateGenerator, UserState}
import com.neurobs.js.util.StringUtilities.dateTimeString
import com.neurobs.react.{ReactElementFactory, ReactUtil, Link}
import com.neurobs.react.semanticui.{UiAccordionFactory, UiButtonFactory, UiContainerFactory}
import com.neurobs.util.{Guid, JsonPrinter}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.ReactElement
import io.circe.generic.auto._
import slinky.core.facade.Hooks.useEffect

import scala.scalajs.js

@react
object SessionInfo {
  case class Props(enclosingElement: (ReactElement*) => ReactElement)
  val component = FunctionalComponent[Props] { props =>

    val sessionSelectionState = SessionSelectionStateGenerator.accessState

    sessionSelectionState.selectedSessionState.value match {
      case None => null
      case Some(session) =>
        SessionInfoView(session, props.enclosingElement)
    }

  }
}

@react
object SessionInfoView {
  case class Props(selectedSession: SessionDisplayData, enclosingElement: (ReactElement*) => ReactElement)
  val component = FunctionalComponent[Props] { props =>

    val userState = UserState.accessState
    val sessionSelectionState = SessionSelectionStateGenerator.accessState

    def isSiteAdmin = userState.userData.isSiteAdmin

    def selectedSession = props.selectedSession

    def sameAdmin = sessionSelectionState.withSameAdmin.isDefined
    def sameStation = sessionSelectionState.withSameStation.isDefined
    def sameStudy = sessionSelectionState.withSameStudy.isDefined

    def admin = props.selectedSession.adminUsername.getOrElse("Unknown")
    def station = props.selectedSession.session.session.stationName
    def study = props.selectedSession.session.session.studyDisplayName
    def testPackageVersionOpt = props.selectedSession.testRuns.headOption.flatMap(_.testPackageVersion)
    def softwareVersionOpt = props.selectedSession.testRuns.headOption.map(_.testRun.softwareVersion)

    def sessionComments = sessionSelectionState.sessionLogCacheState.value.searchCache(props.selectedSession.session.session.sessionId) match {
      case None => List()
      case Some(sessionLogEither) => sessionLogEither match {
        case Left(error) => List()
        case Right(sessionLog) => sessionLog.state.comments.map(_.map(_.text)).getOrElse(List())
      }
    }

    def propertyPTags = selectedSession.session.session.properties.map { p =>
      var primaryPropPairs = Seq[(String, String)]()
      var extraPropPairs = Seq[(String, String)]()

      val primaryPropNames = Seq("Sequence", "Mousing Hand", "Drawing Hand")

      def addPropPair(name: String, value: String) = {
        if (primaryPropNames.contains(name)) primaryPropPairs = primaryPropPairs :+ (name, value)
        else extraPropPairs = extraPropPairs :+ (name, value)
      }

      JsonPrinter.fromJson[TestSessionProperties](p) match {
        case None => addPropPair("Properties", p)
        case Some(props) =>
          addPropPair("Group", props.group)
          addPropPair("Sequence", props.sequence)
          props.config.map(c => addPropPair("Configuration", c))
          for (param <- props.params) addPropPair(param.name, param.value)
      }

      def pairsToElements(pairs: Seq[(String, String)]) = pairs.map(pair => PropertyLabel(pair._1, pair._2))

      (pairsToElements(primaryPropPairs), pairsToElements(extraPropPairs))
    }.getOrElse((Seq[ReactElement](), Seq[ReactElement]()))

    def sessionLabel = {
      dateTimeString(selectedSession.session.session.startTime) + (if (isSiteAdmin) s" (${selectedSession.session.session.sessionId})" else "")
    }

    val allPropertyTags = {

      var primaryInfo = Seq[ReactElement]()
      var extraInfo = Seq[ReactElement]()

      val (primaryPropInfo, extraPropInfo) = propertyPTags

      primaryInfo ++= Seq(PropertyLabel("Session", sessionLabel), PropertyLabel("Id", selectedSession.session.session.subjectGuid.noBracketsString))
      primaryInfo ++= primaryPropInfo

      if (isSiteAdmin) extraInfo ++= Seq(PropertyLabel("Subject GUID", selectedSession.session.session.subjectGuid.noBracketsString))
      if (!sameAdmin) extraInfo ++= Seq(PropertyLabel("Examiner", admin))
      if (!sameStation) extraInfo ++= Seq(PropertyLabel("Station", station))
      if (!sameStudy) extraInfo ++= Seq(PropertyLabel("Study", study))
      extraInfo ++= extraPropInfo
      extraInfo ++= Seq(testPackageVersionOpt match {
        case None => null
        case Some(version) => PropertyLabel("Test Package Version", version)
      })
      extraInfo ++= Seq(softwareVersionOpt match {
        case None => null
        case Some(version) =>
          if (version.isEmpty) null
          else PropertyLabel("Software Version", version)
      })

      (primaryInfo, extraInfo)
    }

    props.enclosingElement(
      allPropertyTags._1 ++
        Seq(SessionLogControls(): ReactElement) ++
        Seq(ExtraInfo(allPropertyTags._2, sessionComments): ReactElement)
    )
  }
}

@react
object SessionLogControls {
  case class Props()
  val component = FunctionalComponent[Props] { _ =>
    val sessionSelectionState = SessionSelectionStateGenerator.accessState
    def selectedSessionOpt = sessionSelectionState.selectedSessionState.value
    def sessionLogCache = sessionSelectionState.sessionLogCache
    def openSessionLogPanel(subjectGuid: Guid, sessionId: Long) = sessionSelectionState.openSessionLogPanel(subjectGuid, sessionId)

    def PanelViewElement(selectedSession: SessionDisplayData) = {
      val sessionId = selectedSession.session.session.sessionId
      val subjectGuid = selectedSession.session.session.subjectGuid
      val pStyle = Seq[(String, js.Any)](("margin", "0px"))
      Link(
        caption = "Session log panel",
        onClick = () => openSessionLogPanel(subjectGuid, sessionId)
      )
    }

    def RawViewElement(selectedSession: SessionDisplayData) = {
      val sessionId = selectedSession.session.session.sessionId
      ReactElementFactory.A(
        text = "Raw session log",
        href = s"/api/v1/test_sessions/$sessionId/log",
        target = "_blank"
      )
    }

    def segmentStyle = Seq[(String, js.Any)](("margin", "0px"), ("padding", "0px"))

    selectedSessionOpt match {
      case None => null
      case Some(selectedSession) =>
        sessionLogCache.searchCache(selectedSession.session.session.sessionId) match {
          case None => null
          case Some(either) => either match {
            case Left(_) => null
            case Right(_) =>
              UiContainerFactory.Segment(
                PanelViewElement(selectedSession)
                //RawViewElement(selectedSession)
              )(("basic", segmentStyle))
          }
        }
    }
  }
}

@react
object ExtraInfo {
  case class Props(extraProperties: Seq[ReactElement], comments: List[String])
  val component = FunctionalComponent[Props] { props =>

    def ExtraPropertiesElement = List(
      UiAccordionFactory.DropdownTitle("Extra Information"),
      UiAccordionFactory.Content(
        props.extraProperties
      )
    )

    def pStyle = Seq[(String, js.Any)](("fontSize", "11px"))
    def CommentsElement = List(
      UiAccordionFactory.DropdownTitle("Comments"),
      UiAccordionFactory.Content(
        props.comments.map(ReactElementFactory.P(_)(pStyle))
      )
    )

    UiAccordionFactory.Accordion(
      if (props.extraProperties.isEmpty) null
      else ExtraPropertiesElement,
      if (props.comments.isEmpty) null
      else CommentsElement
    )
  }
}
